import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { permissionsApi, usersApi } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { UserPlus } from 'lucide-react';

const inviteSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  roleIds: z.array(z.string().uuid()).min(1, 'Select at least one role'),
});

type InviteFormValues = z.infer<typeof inviteSchema>;

export function InviteUserDialog() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);

  const { data: catalog } = useQuery({
    queryKey: ['permissions-catalog'],
    queryFn: () => permissionsApi.getCatalog(),
    enabled: open,
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', roleIds: [] },
  });
  const [submitError, setSubmitError] = useState<string | null>(null);

  const inviteMutation = useMutation({
    mutationFn: (values: InviteFormValues) => {
      if (!user?.tenantId) {
        throw new Error('Your tenant could not be determined. Please sign in again.');
      }
      return usersApi.invite({
        email: values.email,
        tenantId: user.tenantId,
        roleIds: values.roleIds,
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      if (result.emailSent === false) {
        setWarning(
          `Invitation created, but the email could not be sent${
            result.emailError ? `: ${result.emailError}` : '.'
          } You can ask the user to sign in with Google at the hub.`
        );
        reset();
        return;
      }
      setOpen(false);
      reset();
      setWarning(null);
    },
    onError: (err: Error) => {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    },
  });

  const onSubmit = (values: InviteFormValues) => {
    setWarning(null);
    setSubmitError(null);
    inviteMutation.mutate(values);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      reset();
      setWarning(null);
      setSubmitError(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <UserPlus className="mr-2 h-4 w-4" />
          Invite User
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a user</DialogTitle>
          <DialogDescription>
            They'll receive an email and sign in with their Google Workspace account.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="person@rf3concrete.com"
              autoComplete="off"
              {...register('email')}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Roles</Label>
            <Controller
              name="roleIds"
              control={control}
              render={({ field }) => (
                <div className="space-y-2 rounded-md border p-3 max-h-48 overflow-y-auto">
                  {!catalog ? (
                    <p className="text-sm text-muted-foreground">Loading roles…</p>
                  ) : catalog.roles.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No roles available.</p>
                  ) : (
                    catalog.roles.map((role) => {
                      const checked = field.value.includes(role.id);
                      return (
                        <label
                          key={role.id}
                          className="flex items-start gap-2 cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                field.onChange([...field.value, role.id]);
                              } else {
                                field.onChange(field.value.filter((id) => id !== role.id));
                              }
                            }}
                          />
                          <span>
                            <span className="font-medium">{role.name}</span>
                            {role.description && (
                              <span className="block text-xs text-muted-foreground">
                                {role.description}
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              )}
            />
            {errors.roleIds && (
              <p className="text-sm text-destructive">{errors.roleIds.message}</p>
            )}
          </div>

          {submitError && (
            <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-2">
              {submitError}
            </p>
          )}

          {warning && (
            <p className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-md p-2">
              {warning}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={inviteMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={inviteMutation.isPending}>
              {inviteMutation.isPending ? 'Sending…' : 'Send invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
